import { useState, useEffect, useRef, useCallback, memo, useMemo, useContext } from "react";
import { useParams, useLocation } from "react-router-dom";
import { fetchAgentById, runAgent, fetchChatHistory, clearChatHistory, analyzeSurat, updateAgentById } from "../../services/agentService";
import { submitFeedback } from "../../services/feedbackService";
import { AgentContext } from "../../context/AgentContext";
import { useTheme } from "../../context/ThemeContext";
import { useLanguage } from "../../context/LanguageContext";
import { TrashIcon, PaperAirplaneIcon, HandThumbUpIcon, HandThumbDownIcon, PaperClipIcon, XMarkIcon, SparklesIcon } from "@heroicons/react/24/solid";
import { DocumentDuplicateIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import { Check, Copy, FileText, RefreshCw, Pencil } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import AnalysisPanel from "./AnalysisPanel";
import ThinkingIndicator from "./ThinkingIndicator";
import FlowchartRenderer, { isMermaidCode, isArrowFlow } from "./FlowchartRenderer";
import DocumentAttachmentList, { parseUserMessageAttachments } from "./DocumentAttachmentCard";

// ─── RLHF Error Tags ──────────────────────────────────────────────────────────
const ERROR_TAGS = [
  { value: "hukum_salah", label: "Akurasi hukum salah", labelKey: "feedback.tagLegal" },
  { value: "kalkulasi_salah", label: "Kalkulasi salah", labelKey: "feedback.tagCalc" },
  { value: "tidak_relevan", label: "Tidak relevan", labelKey: "feedback.tagIrrelevant" },
  { value: "outdated", label: "Informasi outdated", labelKey: "feedback.tagOutdated" },
  { value: "kurang_lengkap", label: "Jawaban kurang lengkap", labelKey: "feedback.tagIncomplete" },
  { value: "format_buruk", label: "Format / penyajian buruk", labelKey: "feedback.tagFormat" },
];

function formatTime(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Jakarta" });
}

function formatDateSeparator(ts, isEnglish = false) {
  if (!ts) return "";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "";
  
  const options = { timeZone: "Asia/Jakarta", year: "numeric", month: "2-digit", day: "2-digit" };
  const strMsg = d.toLocaleDateString("en-CA", options);
  
  const now = new Date();
  const strToday = now.toLocaleDateString("en-CA", options);
  
  const yest = new Date(now.getTime() - 86400000);
  const strYest = yest.toLocaleDateString("en-CA", options);

  if (strMsg === strToday) {
    return isEnglish ? "Today" : "Hari Ini";
  } else if (strMsg === strYest) {
    return isEnglish ? "Yesterday" : "Kemarin";
  } else {
    return d.toLocaleDateString(isEnglish ? "en-US" : "id-ID", {
      day: "2-digit",
      month: isEnglish ? "short" : "2-digit",
      year: "numeric",
      timeZone: "Asia/Jakarta",
    });
  }
}

// ─── Feedback Buttons Component ───────────────────────────────────────────────
function FeedbackButtons({ messageId, question, answer, agentId: fbAgentId, intent }) {
  const { t, isIndonesian, isEnglish } = useLanguage();
  const [status, setStatus] = useState("neutral"); // neutral | liked | disliked | submitted
  const [showForm, setShowForm] = useState(false);
  const [errorTags, setErrorTags] = useState([]);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(answer).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleThumbUp = async () => {
    if (status !== "neutral") return;
    setStatus("liked");
    try {
      await submitFeedback({
        chat_history_id: messageId,
        agent_id: fbAgentId,
        question, answer,
        rating: "up",
        error_tag: null,
        comment: null,
        intent,
      });
    } catch (e) {
      console.error("Feedback error:", e);
    }
  };

  const handleThumbDown = () => {
    if (status !== "neutral") return;
    setStatus("disliked");
    setShowForm(true);
  };

  const handleSubmitFeedback = async () => {
    setSubmitting(true);
    try {
      await submitFeedback({
        chat_history_id: messageId,
        agent_id: fbAgentId,
        question, answer,
        rating: "down",
        error_tag: errorTags.length > 0 ? errorTags.join(", ") : null,
        comment: comment || null,
        intent,
      });
      setStatus("submitted");
      setShowForm(false);
    } catch (e) {
      console.error("Feedback error:", e);
    } finally {
      setSubmitting(false);
    }
  };

  if (status === "submitted") {
    return (
      <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-text-sec">
        <Check className="w-3.5 h-3.5 text-emerald-500" />
        <span>{t("feedback.feedbackSent")}</span>
      </div>
    );
  }

  return (
    <div className="mt-1.5">
      {/* Action buttons */}
      <div className="flex items-center gap-1">
        <button
          onClick={handleCopy}
          className="p-1.5 rounded-md text-text-sec hover:text-brand hover:bg-surface-hover transition-all"
          title={t("feedback.copyResponse")}
        >
          {copied ? (
            <span className="text-[10px] text-emerald-600 font-medium px-1 flex items-center gap-1">
              <Check className="w-3.5 h-3.5" /> {isIndonesian ? "Tersalin!" : "Copied!"}
            </span>
          ) : (
            <Copy className="w-3.5 h-3.5" />
          )}
        </button>
        <button
          onClick={handleThumbUp}
          disabled={status !== "neutral"}
          className={`p-1.5 rounded-full transition-all ${
            status === "liked"
              ? "text-emerald-500 bg-emerald-500/20"
              : "text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/15"
          } disabled:cursor-default`}
          title={isEnglish ? "Good response" : "Jawaban bagus"}
        >
          <HandThumbUpIcon className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={handleThumbDown}
          disabled={status !== "neutral"}
          className={`p-1.5 rounded-full transition-all ${
            status === "disliked"
              ? "text-rose-500 bg-rose-500/20"
              : "text-slate-400 hover:text-rose-400 hover:bg-rose-500/15"
          } disabled:cursor-default`}
          title={isEnglish ? "Unsatisfactory response" : "Jawaban kurang tepat"}
        >
          <HandThumbDownIcon className="w-3.5 h-3.5" />
        </button>
        {status === "liked" && (
          <span className="text-[11px] text-emerald-500 font-medium ml-1">
            {isEnglish ? "Thank you!" : "Terima kasih!"}
          </span>
        )}
      </div>

      {/* Feedback form — muncul setelah thumbs down */}
      {showForm && (
        <div className="mt-2 p-2.5 bg-feedback-bg rounded-lg border border-ledger-border space-y-2">
          <p className="text-[11px] text-text-sec font-medium">
            {isEnglish ? "What was unsatisfactory?" : "Apa yang kurang tepat?"}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {ERROR_TAGS.map((tag) => {
              const isSelected = errorTags.includes(tag.value);
              return (
                <button
                  key={tag.value}
                  onClick={() => {
                    if (isSelected) {
                      setErrorTags(errorTags.filter((t) => t !== tag.value));
                    } else {
                      setErrorTags([...errorTags, tag.value]);
                    }
                  }}
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition-all font-medium ${
                    isSelected
                      ? "bg-red-500/10 border-red-500/30 text-red-500 shadow-sm"
                      : "bg-card-bg border-ledger-border text-text-sec hover:text-text-pri hover:bg-surface-hover"
                  }`}
                >
                  {tag.labelKey ? t(tag.labelKey, tag.label) : tag.label}
                </button>
              );
            })}
          </div>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t("feedback.commentPlaceholder")}
            rows={2}
            className="w-full text-[12px] p-2 rounded-lg bg-input-bg text-text-pri border border-ledger-border focus:outline-none focus:ring-1 focus:ring-red-400 resize-none placeholder:text-muted"
          />
          <div className="flex gap-2">
            <button
              onClick={handleSubmitFeedback}
              disabled={submitting}
              className="text-[11px] px-3 py-1 rounded-lg bg-verified text-white hover:bg-verified/90 disabled:opacity-50"
            >
              {submitting ? t("feedback.submitting") : t("feedback.submit")}
            </button>
            <button
              onClick={() => { setShowForm(false); setStatus("neutral"); }}
              className="text-[11px] px-3 py-1 rounded-lg text-text-sec hover:bg-surface-hover hover:text-text-pri"
            >
              {t("feedback.cancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── LaTeX Preprocessor ───────────────────────────────────────────────────────
// Converts LaTeX-style math notation into clean readable text
// before ReactMarkdown touches it.

export function cleanLatexExpr(expr) {
  return expr
    .replace(/\\text\{([^}]+)\}/g, "$1")                          // \text{X} → X
    .replace(/\\mathbf\{([^}]+)\}/g, "$1")                        // \mathbf{X} → X
    .replace(/\\mathrm\{([^}]+)\}/g, "$1")                        // \mathrm{X} → X
    .replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, "($1) / ($2)")      // \frac{a}{b} → (a) / (b)
    .replace(/\\times/g, "×")
    .replace(/\\div/g, "÷")
    .replace(/\\cdot/g, "·")
    .replace(/\\leq/g, "≤")
    .replace(/\\geq/g, "≥")
    .replace(/\\neq/g, "≠")
    .replace(/\\approx/g, "≈")
    .replace(/\\infty/g, "∞")
    .replace(/\\sqrt\{([^}]+)\}/g, "√($1)")
    .replace(/\\_/g, "_")
    .replace(/\^/g, "^")
    .replace(/\{|\}/g, "")                                         // strip remaining braces
    .replace(/\s+/g, " ")
    .trim();
}

export function preprocessConsultationText(text) {
  if (typeof text !== "string") return "";

  let cleaned = text.replace(/\r\n/g, "\n");

  // 0. Replace raw HTML <br> tags with double newline
  cleaned = cleaned.replace(/<br\s*\/?>/gi, "\n\n");

  // Strip accidental SVG / pipeline button text artifacts if leaked anywhere in text
  cleaned = cleaned.replace(/(?:svg)?\s*\*{0,2}\s*(?:Analisis|Tanggapan|Kajian|Draf|Kalkulasi|Konsultasi|Bantuan)\s+[^\n*]+—[^\n*]+\*{0,2}\s*(?:\d+(?:\.\d+)?s)?\s*(?:svg)?/gi, "");
  cleaned = cleaned.replace(/svg\s*\*{0,2}[^\n*]+—[^\n*]+\*{0,2}\s*(?:\d+(?:\.\d+)?s)?\s*svg?/gi, "");
  cleaned = cleaned.replace(/(?:\n+)?\s*\*{0,2}(?:Analisis|Tanggapan|Kajian|Draf|Kalkulasi|Konsultasi|Bantuan)[^\n*]+—[^\n*]+\*{0,2}\s*\n+\s*\d+(?:\.\d+)?s\s*$/gi, "");

  // 1. Replace raw unicode bullets (•, ●, ○) with standard markdown list markers
  cleaned = cleaned.replace(/^[ \t]*[•●○]\s*/gm, "- ");

  // 2. Fix dangling numbered list headers where LLM outputs "4.\nTitle:" or "4. \n\nTitle:"
  cleaned = cleaned.replace(/(?:^|\n)\s*(\d+)[\.\)]\s*\n+([A-Za-z0-9\u00C0-\u024F][^\n\r]+)/g, "\n\n$1. $2\n");

  // 3b. Format sub-poin pos sengketa (misal: "b. Biaya pengolahan — sampling...") agar tebal & berjarak rapi
  cleaned = cleaned.replace(/(?:^|\n)\s*([a-z])[\.\)]\s+([A-Za-z0-9\u00C0-\u024F][^\n\r]+)(?=\n|$)/g, (match, letter, title) => {
    const trimmedTitle = title.trim();
    if (trimmedTitle.startsWith("**") || trimmedTitle.startsWith("#")) {
      return match;
    }
    if (trimmedTitle.length > 110 || trimmedTitle.endsWith(".")) {
      return match;
    }
    return `\n\n**${letter}. ${trimmedTitle}**\n\n`;
  });

  // 4. Highlight executive sections like Kesimpulan, Dasar Hukum, Catatan, Rekomendasi
  cleaned = cleaned.replace(/(?:^|\n)\s*(Kesimpulan|Dasar Hukum|Catatan|Rekomendasi|Perhatian|Langkah Perhitungan|Ringkasan Eksekutif):\s*/gi, "\n\n### $1:\n");

  // 5. Highlight calculation total lines starting with "Total ... = Rp..." as callout blockquotes
  cleaned = cleaned.replace(/(?:^|\n)(?![>\-#*])(Total [^\n=]+=[^\n]+)/g, "\n\n> **$1**\n\n");

  // 6. Convert inline numbering like ": (1) foo; (2) bar; dan (3) baz" to Markdown lists
  cleaned = cleaned.replace(/(^|[:;.])\s*(?:dan\s+|atau\s+)?\((\d+)\)\s+(?!(?:dan|atau)\s+\()([A-Za-z])/gim, (match, punc, digit, firstLetter) => {
    if (!punc) return `${digit}. ${firstLetter}`;
    let prefix = (punc === ':' || punc === '.') ? `${punc}\n\n` : '\n';
    return `${prefix}${digit}. ${firstLetter}`;
  });

  // 7. LaTeX math expressions
  cleaned = cleaned.replace(/\\\[([\s\S]+?)\\\]/g, (_, expr) => `\n\`${cleanLatexExpr(expr)}\`\n`);
  cleaned = cleaned.replace(/\[([^[\]]*\\[^[\]]*)\]/g, (_, expr) => `\`${cleanLatexExpr(expr)}\``);
  cleaned = cleaned.replace(/\\\((.+?)\\\)/g, (_, expr) => `\`${cleanLatexExpr(expr)}\``);

  // 7b. Convert horizontal procedural arrow sequences (e.g. A \rightarrow B \rightarrow C, or A -> B -> C) into flowchart blocks
  cleaned = cleaned.replace(
    /(?:^|\n)\s*(?:\$\$|\\\[)?\s*([^\n$]{3,}?(?:\\rightarrow|\\to|→|-->|->)[^\n$]{3,}?(?:\\rightarrow|\\to|→|-->|->)[^\n$]{2,}?)\s*(?:\$\$|\\\])?(?=\n|$)/gi,
    (match, flowContent) => {
      const cleanFlow = flowContent
        .replace(/\\text\{([^}]+)\}/g, "$1")
        .replace(/\\mathrm\{([^}]+)\}/g, "$1")
        .replace(/\$/g, "")
        .trim();
      const parts = cleanFlow.split(/\s*(?:\\rightarrow|\\to|→|-->|->)\s*/).map((p) => p.trim()).filter(Boolean);
      if (parts.length >= 2) {
        return `\n\n\`\`\`flowchart\n${parts.join("\n↓\n")}\n\`\`\`\n\n`;
      }
      return match;
    }
  );

  // 8. Auto-wrap vertical arrow flow sequences (↓, ⬇, -->, ->) into flowchart blocks
  if (/[↓⬇▼]|(?:^|\n)\s*-->\s*(?:\n|$)/.test(cleaned)) {
    const lines = cleaned.split("\n");
    let inCode = false;
    let inFlow = false;
    let flowLines = [];
    let newLines = [];

    const hasArrowNearby = (idx) => {
      for (let j = Math.max(0, idx - 3); j <= Math.min(lines.length - 1, idx + 3); j++) {
        if (/^\s*[↓⬇▼]\s*$/.test(lines[j]) || /^\s*-->\s*$/.test(lines[j])) return true;
      }
      return false;
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (line.trim().startsWith("```")) {
        inCode = !inCode;
        newLines.push(line);
        continue;
      }
      if (inCode) {
        newLines.push(line);
        continue;
      }

      const isArrow = /^\s*[↓⬇▼]\s*$/.test(line) || /^\s*-->\s*$/.test(line) || /^\s*->\s*$/.test(line);

      if (isArrow) {
        inFlow = true;
        flowLines.push(line);
      } else if (inFlow) {
        if (line.trim().startsWith("#") || (line.trim() === "" && !hasArrowNearby(i))) {
          const arrowCount = flowLines.filter(l => /^\s*[↓⬇▼]\s*$/.test(l) || /^\s*-->\s*$/.test(l)).length;
          if (arrowCount >= 2) {
            newLines.push("```flowchart");
            newLines.push(...flowLines);
            newLines.push("```");
          } else {
            newLines.push(...flowLines);
          }
          flowLines = [];
          inFlow = false;
          newLines.push(line);
        } else {
          flowLines.push(line);
        }
      } else if (hasArrowNearby(i) && line.trim().length > 0) {
        const willHaveArrow = (i + 1 < lines.length && /^\s*[↓⬇▼]\s*$/.test(lines[i + 1])) ||
                              (i + 2 < lines.length && /^\s*[↓⬇▼]\s*$/.test(lines[i + 2]));
        if (willHaveArrow) {
          inFlow = true;
          flowLines.push(line);
        } else {
          newLines.push(line);
        }
      } else {
        newLines.push(line);
      }
    }

    if (inFlow) {
      const arrowCount = flowLines.filter(l => /^\s*[↓⬇▼]\s*$/.test(l) || /^\s*-->\s*$/.test(l)).length;
      if (arrowCount >= 2) {
        newLines.push("```flowchart");
        newLines.push(...flowLines);
        newLines.push("```");
      } else {
        newLines.push(...flowLines);
      }
    }

    cleaned = newLines.join("\n");
  }

  // 9. Clean excessive blank lines
  cleaned = cleaned.replace(/\n{3,}/g, "\n\n");

  return cleaned.trim();
}

// Backwards compatibility alias
export const preprocessMath = preprocessConsultationText;

// ─── Executive Typography Markdown Components ─────────────────────────────────
export const mdComponents = {
  p: ({ children }) => (
    <p className="text-text-pri text-[14.5px] sm:text-[15px] leading-[1.8] mb-3.5 last:mb-0 font-normal text-left">
      {children}
    </p>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold text-text-pri text-[inherit]">
      {children}
    </strong>
  ),
  em: ({ children }) => (
    <em className="italic text-text-sec text-[inherit]">{children}</em>
  ),
  h1: ({ children }) => (
    <h1 className="text-lg sm:text-xl font-bold text-text-pri mt-5 mb-2.5 pb-2 border-b border-ledger-border" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-base sm:text-lg font-bold text-text-pri mt-4 mb-2" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-[15px] sm:text-[16px] font-bold text-text-pri mt-4 mb-2 flex items-center gap-2" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <span className="w-1.5 h-4 bg-brand rounded-full inline-block shrink-0"></span>
      <span>{children}</span>
    </h3>
  ),
  ul: ({ children }) => (
    <ul className="list-disc list-outside ml-6 space-y-2 my-3 text-[14.5px] sm:text-[15px] leading-[1.8] text-text-pri">
      {children}
    </ul>
  ),
  ol: ({ start, children }) => (
    <ol start={start} className="list-decimal list-outside ml-6 space-y-2 my-3 text-[14.5px] sm:text-[15px] leading-[1.8] text-text-pri">
      {children}
    </ol>
  ),
  li: ({ children }) => (
    <li className="text-[14.5px] sm:text-[15px] leading-[1.8] text-text-pri pl-1 marker:text-text-sec [&>p]:!my-0.5 text-left">
      {children}
    </li>
  ),
  code: ({ node, className, children, ...props }) => {
    const isBlock = node?.tagName === "code" && node?.properties?.className;
    const match = /language-(\w+)/.exec(className || "");
    const lang = match ? match[1].toLowerCase() : "";
    const rawText = Array.isArray(children) ? children.join("") : String(children || "");

    if (lang === "mermaid" || isMermaidCode(rawText)) {
      return <FlowchartRenderer content={rawText} />;
    }
    if (lang === "flowchart" || isArrowFlow(rawText)) {
      return <FlowchartRenderer content={rawText} />;
    }

    return !isBlock ? (
      <code className="bg-paper text-brand px-1.5 py-0.5 rounded font-mono text-[13px] border border-ledger-border font-semibold" {...props}>
        {children}
      </code>
    ) : (
      <code className="block bg-paper text-text-pri p-3.5 rounded-xl font-mono text-[13px] border border-ledger-border overflow-x-auto my-3 leading-relaxed" {...props}>
        {children}
      </code>
    );
  },
  pre: ({ children }) => {
    const extractRaw = (node) => {
      if (!node) return "";
      if (typeof node === "string") return node;
      if (Array.isArray(node)) return node.map(extractRaw).join("");
      if (node?.props?.children) return extractRaw(node.props.children);
      return String(node);
    };

    const rawText = extractRaw(children);
    if (isMermaidCode(rawText) || isArrowFlow(rawText)) {
      return <FlowchartRenderer content={rawText} />;
    }

    return (
      <pre className="bg-paper border border-ledger-border rounded-xl p-3 my-3 overflow-x-auto font-mono text-[13px]">
        {children}
      </pre>
    );
  },
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-brand bg-brand-low/50 px-4 py-3 rounded-r-xl my-3.5 text-text-pri font-medium shadow-2xs text-left">
      {children}
    </blockquote>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto my-4 rounded-xl border border-ledger-border bg-paper shadow-2xs">
      <table className="w-full text-xs sm:text-[13px] text-left divide-y divide-ledger-border">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-card-bg text-text-sec uppercase tracking-wider text-[11px] font-semibold">
      {children}
    </thead>
  ),
  tbody: ({ children }) => (
    <tbody className="divide-y divide-ledger-border/40">
      {children}
    </tbody>
  ),
  tr: ({ children }) => (
    <tr className="hover:bg-surface-hover/50 transition-colors">
      {children}
    </tr>
  ),
  th: ({ children }) => (
    <th className="px-4 py-2.5 whitespace-nowrap font-semibold text-text-sec">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="px-4 py-2.5 font-mono whitespace-nowrap text-text-pri">
      {children}
    </td>
  ),
  hr: () => <hr className="border-ledger-border my-4" />,
};

// ─── Memoized Chat Bubble ── Clean & Polosan like ChatGPT / Claude
const ChatBubble = memo(function ChatBubble({ msg, idx, isLast, isDownloadReady, handleDownloadDoc, downloadingDoc, formatTime, preprocessMath, mdComponents, userPrompt, onReload, loadingResponse }) {
  const { t, isEnglish } = useLanguage();
  const isUser = msg.role === "user";
  const [userCopied, setUserCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState("");

  // Parse file attachments and clean user message text (unconditional hook call)
  const rawContent = typeof msg.content === "string" ? msg.content : JSON.stringify(msg.content);
  const { attachments, textOnly } = useMemo(
    () => (isUser ? parseUserMessageAttachments(rawContent) : { attachments: [], textOnly: "" }),
    [isUser, rawContent]
  );

  if (isUser) {
    const handleUserCopy = () => {
      navigator.clipboard.writeText(rawContent).then(() => {
        setUserCopied(true);
        setTimeout(() => setUserCopied(false), 2000);
      });
    };

    const handleStartEdit = () => {
      setEditText(textOnly || rawContent);
      setIsEditing(true);
    };

    const handleCancelEdit = () => {
      setIsEditing(false);
      setEditText("");
    };

    const handleSaveAndSend = () => {
      const trimmed = editText.trim();
      if (!trimmed || loadingResponse) return;
      setIsEditing(false);
      if (onReload) {
        let finalContentToSend = trimmed;
        if (attachments.length > 0 && textOnly) {
          const prefix = rawContent.slice(0, rawContent.length - textOnly.length).trim();
          if (prefix) {
            finalContentToSend = `${prefix}\n\n${trimmed}`;
          }
        }
        onReload(finalContentToSend, idx);
      }
    };

    const handleEditKeyDown = (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSaveAndSend();
      } else if (e.key === "Escape") {
        e.preventDefault();
        handleCancelEdit();
      }
    };

    if (isEditing) {
      return (
        <div className="flex flex-col items-end z-10 w-full mb-3">
          <div className="w-full max-w-[90%] sm:max-w-[85%] lg:max-w-[720px] rounded-2xl sm:rounded-3xl p-4 shadow-sm bg-card-bg border border-brand/50 text-text-pri">
            {attachments.length > 0 && (
              <div className="mb-2.5">
                <DocumentAttachmentList attachments={attachments} />
              </div>
            )}
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onKeyDown={handleEditKeyDown}
              rows={Math.min(12, Math.max(3, editText.split("\n").length))}
              className="w-full bg-paper border border-ledger-border rounded-xl p-3 text-[14px] sm:text-[14.5px] leading-relaxed text-text-pri focus:outline-none focus:ring-1.5 focus:ring-brand font-medium resize-y"
              autoFocus
            />
            <div className="flex items-center justify-end gap-2 mt-2.5">
              <button
                type="button"
                onClick={handleCancelEdit}
                className="px-3 py-1.5 rounded-lg text-xs font-medium text-text-sec hover:text-text-pri hover:bg-surface-hover transition-colors"
              >
                {t("chat.cancelEdit") || (isEnglish ? "Cancel" : "Batal")}
              </button>
              <button
                type="button"
                onClick={handleSaveAndSend}
                disabled={!editText.trim() || loadingResponse}
                className="px-3.5 py-1.5 rounded-lg text-xs font-semibold bg-brand text-white hover:bg-brand-hover transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-xs"
              >
                {t("chat.saveAndResend") || (isEnglish ? "Send" : "Kirim")}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-end z-10 w-full mb-3 group/user">
        <div className="max-w-[90%] sm:max-w-[85%] lg:max-w-[720px] rounded-2xl px-4 py-3 bg-surface-inset text-text-pri border border-ledger-border shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
          {/* File attachment cards (ChatGPT / Claude style) */}
          {attachments.length > 0 && (
            <DocumentAttachmentList attachments={attachments} />
          )}

          {/* User text */}
          {textOnly && (
            <div 
              style={{ fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}
              className={`text-[14.5px] sm:text-[15px] leading-relaxed text-text-pri font-medium whitespace-pre-wrap break-words ${
                attachments.length > 0 ? "pt-2.5 mt-1 border-t border-brand/15" : ""
              }`}
            >
              {textOnly}
            </div>
          )}

          <div className="flex justify-end mt-1 text-[10px] font-mono text-text-sec/60 select-none">
            {formatTime(msg.timestamp)}
          </div>
        </div>

        {/* User action buttons: copy + edit + reload */}
        <div className="flex items-center gap-0.5 mt-1 mr-1 opacity-0 group-hover/user:opacity-100 transition-opacity">
          <button
            onClick={handleUserCopy}
            className="p-1 rounded-md text-text-sec/50 hover:text-brand hover:bg-surface-hover transition-all"
            title={t("chat.copyMessage")}
          >
            {userCopied ? (
              <Check className="w-3 h-3 text-emerald-500" />
            ) : (
              <Copy className="w-3 h-3" />
            )}
          </button>
          {onReload && (
            <button
              onClick={handleStartEdit}
              disabled={loadingResponse}
              className="p-1 rounded-md text-text-sec/50 hover:text-brand hover:bg-surface-hover transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              title={t("chat.editMessage") || (isEnglish ? "Edit message" : "Edit pesan")}
            >
              <Pencil className="w-3 h-3" />
            </button>
          )}
          {onReload && (
            <button
              onClick={() => {
                if (!loadingResponse) {
                  const plainText = textOnly || rawContent;
                  if (plainText) onReload(plainText, idx);
                }
              }}
              disabled={loadingResponse}
              className="p-1 rounded-md text-text-sec/50 hover:text-brand hover:bg-surface-hover transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              title={t("chat.resendMessage")}
            >
              <RefreshCw className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // Assistant Bubble (Clean & Polosan like ChatGPT / Claude)
  return (
    <div className="flex flex-col items-start z-10 w-full mb-4">
      <div className="w-full max-w-[96%] sm:max-w-[92%] lg:max-w-[820px] py-2 px-1 text-text-pri min-w-0 overflow-hidden">
        {/* Content with exact IBM Plex Sans styling and OpenType features */}
        <div 
          style={{ 
            fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            fontFeatureSettings: "'cv02', 'cv03', 'cv04', 'cv11', 'tnum'"
          }}
          className="text-[14.5px] sm:text-[15px] leading-[1.8] text-text-pri break-words min-w-0 overflow-x-auto"
        >
          <ReactMarkdown 
            remarkPlugins={[remarkGfm, remarkMath]} 
            rehypePlugins={[rehypeKatex]} 
            components={mdComponents}
          >
            {preprocessConsultationText(
              typeof msg.content === "object"
                ? JSON.stringify(msg.content)
                : msg.content
            )}
          </ReactMarkdown>
        </div>

        {/* Download .docx action button: strictly gated by structured readiness */}
        {msg.role === "assistant" &&
          isDownloadReady(msg) && (
          <div className="mt-4 p-3.5 rounded-xl docx-ready-card flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-xs text-text-sec">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span>Draf surat litigasi siap diekspor ke format resmi Word</span>
            </div>
            <button
              onClick={() => handleDownloadDoc(msg.content, userPrompt, msg.doc_type, msg.session_id)}
              disabled={downloadingDoc}
              className="flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-medium bg-text-pri text-paper hover:opacity-90 disabled:opacity-50 transition-all cursor-pointer"
            >
              {downloadingDoc ? (
                <>
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="32" strokeDashoffset="12"/>
                  </svg>
                  <span>{t("chat.creatingFile") || "Membuat dokumen Word..."}</span>
                </>
              ) : (
                <>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                    <polyline points="14 2 14 8 20 8" />
                    <path d="M12 18v-6" />
                    <path d="m9 15 3 3 3-3" />
                  </svg>
                  <span>{t("chat.downloadDocx") || "Download Dokumen Word (.docx)"}</span>
                </>
              )}
            </button>
          </div>
        )}

        {/* Download XML action button */}
        {msg.role === "assistant" && msg.xml_content && (
          <button
            onClick={() => {
              const blob = new Blob([msg.xml_content], { type: 'application/xml' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              const filename = msg.xml_file ? msg.xml_file.split(/[/\\]/).pop() : `SPT_L3B_${new Date().toISOString().slice(0,10)}.xml`;
              a.href = url;
              a.download = filename;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
            }}
            className="mt-3.5 flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold bg-verified text-white hover:bg-verified/90 shadow-sm transition-all"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd"/>
            </svg>
            {t("chat.downloadXml")}
          </button>
        )}

        {/* Timestamp */}
        <div className="flex justify-end mt-2 text-[10px] font-mono text-text-sec/50 select-none">
          {formatTime(msg.timestamp)}
        </div>
      </div>
    </div>
  );
});

const ADMIN_EMAILS = ["alfa271200@gmail.com"];

// ─── Main Component ───────────────────────────────────────────────────────────

export default function PlaygroundTab({ agentId: agentIdProp }) {
  const params = useParams();
  const agentId = agentIdProp || params.agentId;
  const { refreshAgents } = useContext(AgentContext);
  const { isLight } = useTheme();
  const { t, isEnglish, isIndonesian, language } = useLanguage();
  const [formData, setFormData] = useState({});
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  const [isAdmin, setIsAdmin] = useState(() => {
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        const email = (parsed.email || "").toLowerCase();
        return ADMIN_EMAILS.includes(email);
      }
    } catch (e) {}
    return false;
  });

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loadingResponse, setLoadingResponse] = useState(false);
  // Track last document draft for .docx download
  const [lastDraft, setLastDraft] = useState(null);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [downloadingDoc, setDownloadingDoc] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const dragCounterRef = useRef(0);
  const autoSentRef = useRef(false);
  const sendMessageRef = useRef(null);
  const uploadFilesRef = useRef(null);
  const location = useLocation();

  // Reset autoSentRef when agent changes
  useEffect(() => {
    autoSentRef.current = false;
  }, [agentId]);

  // ── Accepted file types for drag & drop ──
  const ACCEPTED_TYPES = [
    'application/pdf',
    'image/png', 'image/jpeg', 'image/webp',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
  ];
  const ACCEPTED_EXTENSIONS = ['.pdf', '.png', '.jpg', '.jpeg', '.webp', '.docx', '.doc', '.xlsx', '.xls'];

  const isFileAccepted = (file) => {
    if (ACCEPTED_TYPES.includes(file.type)) return true;
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    return ACCEPTED_EXTENSIONS.includes(ext);
  };

  const addPendingFiles = (newFiles) => {
    if (!newFiles || newFiles.length === 0) return;
    const accepted = Array.from(newFiles).filter(isFileAccepted);
    if (accepted.length === 0) return;
    setPendingFiles((prev) => {
      const existingKeys = new Set(prev.map(f => `${f.name}-${f.size}`));
      const toAdd = accepted.filter(f => !existingKeys.has(`${f.name}-${f.size}`));
      return [...prev, ...toAdd].slice(0, 10); // max 10 files
    });
  };

  const removePendingFile = (indexToRemove) => {
    setPendingFiles((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  // ── Drag & Drop handlers ──
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounterRef.current = 0;
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addPendingFiles(e.dataTransfer.files);
    }
  };

  // ── Paste handler (for pasting images/files from clipboard) ──
  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const pasted = [];
    for (const item of items) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file && isFileAccepted(file)) {
          pasted.push(file);
        }
      }
    }
    if (pasted.length > 0) {
      e.preventDefault();
      addPendingFiles(pasted);
    }
  };

  // Auto-resize textarea when input changes without ugly scrollbar
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      const nextHeight = Math.min(Math.max(textareaRef.current.scrollHeight, 44), 120);
      textareaRef.current.style.height = nextHeight + "px";
      textareaRef.current.style.overflowY = textareaRef.current.scrollHeight > 120 ? "auto" : "hidden";
    }
  }, [input]);

  // Structured check for document download readiness
  const isDownloadReady = (msg) => {
    if (!msg || msg.role !== "assistant") return false;

    // If pre-generated docx is present from backend, it is ready immediately
    if (msg.docx_file) return true;

    // 1. Critical intake requirement gate: strictly blocked while critical data is required
    if (msg.verification_status === "INTAKE_CRITICAL_DATA_REQUIRED") return false;

    // 2. Intake blocker phrases
    const content = typeof msg.content === "string" ? msg.content : "";
    if (content.includes("sebelum draf surat resmi dapat disusun")) return false;
    if (content.includes("mohon lengkapi informasi penting berikut")) return false;

    // 3. Must have actual draft indicators
    const hasLetterBody = isDraftComplete(content);

    // If marked purely as analysis and does NOT contain a formal draft letter body, do not show download
    if (msg.isAnalysis && !hasLetterBody) return false;

    // If backend explicitly marked this as consultation AND does not contain a formal letter body, do not show download
    if (msg.doc_type === "consultation" && !hasLetterBody) return false;

    // If missing critical fields are reported but draft is complete (working/placeholder draft), allow download
    if (Array.isArray(msg.missing_critical_fields) && msg.missing_critical_fields.length > 0 && !hasLetterBody) return false;

    return hasLetterBody || (msg.intent === "DOCUMENT_DRAFTING" && msg.doc_type && msg.doc_type !== "consultation");
  };

  // Helper to detect draft text presence for tracking
  const isDraftComplete = (text) => {
    if (typeof text !== "string" || text.trim().length < 300) return false;
    const lower = text.toLowerCase();

    // If it is merely an analysis or consultation ending with an offer to draft, it is NOT a draft yet
    const hasOfferingOnly = 
      (lower.includes("ingin saya buatkan draft") || lower.includes("apakah anda ingin saya buatkan draft") || lower.includes("analisis eksekutif")) &&
      !lower.includes("hormat kami") && !lower.includes("hormat saya") && !lower.includes("pemberi kuasa");
    if (hasOfferingOnly) return false;

    // Special check for Surat Kuasa Khusus (does not use "Dengan hormat" / "Kepada Yth")
    const isSuratKuasa =
      (lower.includes("surat kuasa khusus") || lower.includes("surat kuasa")) &&
      (lower.includes("memberikan kuasa") || lower.includes("penerima kuasa")) &&
      (lower.includes("pemberi kuasa") || lower.includes("demikian surat kuasa"));
    if (isSuratKuasa) return true;

    // A genuine formal tax brief / letter must have both formal opening and closing
    const hasOpening = 
      lower.includes("dengan hormat") || 
      lower.includes("kepada yth") ||
      lower.includes("yth.") ||
      lower.includes("yth\n") ||
      lower.includes("yth ");
      
    const hasClosing = 
      lower.includes("hormat kami") || 
      lower.includes("hormat saya") || 
      lower.includes("demikian tanggapan ini kami sampaikan") || 
      lower.includes("demikian permohonan ini") ||
      lower.includes("demikian surat ini") ||
      lower.includes("demikian surat permohonan ini") ||
      lower.includes("meterai sesuai ketentuan");

    return hasOpening && hasClosing;
  };

  // Detect doc type from draft content
  const detectDocType = (text) => {
    if (!text) return "dokumen";
    const t = text.toLowerCase();
    if (t.includes("surat kuasa khusus") || (t.includes("surat kuasa") && (t.includes("pemberi kuasa") || t.includes("penerima kuasa") || t.includes("memberikan kuasa")))) return "surat_kuasa";
    if (t.includes("pembatalan stp") || t.includes("pembatalan surat tagihan pajak") || t.includes("pengurangan atau pembatalan surat tagihan pajak") || t.includes("pasal 36 ayat (1) huruf c")) return "pembatalan_stp";
    if (t.includes("memori peninjauan kembali") || t.includes("memori pk")) return "memori_pk";
    if (t.includes("kontra memori")) return "kontra_pk";
    if (t.includes("surat gugatan") || t.includes("mengajukan gugatan") || t.includes("permohonan gugatan")) return "gugatan";
    if (t.includes("permohonan banding") || t.includes("surat banding") || t.includes("mengajukan banding")) return "banding";
    if (t.includes("hasil penelitian keberatan") || t.includes("tanggapan keberatan") || t.includes("spuh") || t.includes("pemberitahuan untuk hadir") || t.includes("daftar hasil penelitian")) return "spuh";
    if (t.includes("tanggapan sphp") || t.includes("tanggapan atas surat pemberitahuan hasil pemeriksaan") || t.includes("sphp") || t.includes("pemberitahuan hasil pemeriksaan") || t.includes("temuan pemeriksa") || t.includes("temuan pemeriksaan") || t.includes("menjawab hasil temuan")) return "sphp";
    if (t.includes("sp2dk") || t.includes("tanggapan sp2dk") || t.includes("klarifikasi data")) return "sp2dk";

    const isExplicitKeberatan = t.includes("surat keberatan") || t.includes("keberatan atas skpkb") || t.includes("keberatan atas skp") || t.includes("mengajukan keberatan") || t.includes("hal : keberatan") || t.includes("hal: keberatan") || t.includes("perihal: keberatan") || t.includes("permohonan keberatan") || t.includes("buatkan surat keberatan") || t.includes("draft surat keberatan");
    if (isExplicitKeberatan) {
      if (t.includes("pbb") || t.includes("bumi dan bangunan")) return "keberatan_pbb";
      return "keberatan";
    }

    if (t.includes("quality assurance") || t.includes("qa pemeriksaan")) return "quality_assurance";
    if (t.includes("salinan putusan") || t.includes("pengiriman kembali")) return "salinan_putusan";
    if (t.includes("pengungkapan ketidakbenaran")) return "pengungkapan";
    if (t.includes("permohonan pembetulan") || t.includes("surat pembetulan")) return "pembetulan";
    if (t.includes("permohonan pencabutan") || t.includes("pencabutan keberatan")) return "pencabutan";
    if (t.includes("permohonan pengurangan") || t.includes("pengurangan sanksi") || t.includes("penghapusan sanksi") || t.includes("pengurangan atau penghapusan") || t.includes("pengurangan atau pembatalan") || t.includes("pengurangan denda")) return "pengurangan";

    if (t.includes("pengadilan pajak") || t.includes("banding")) return "banding";
    if (t.includes("keberatan")) return "keberatan";
    if (t.includes("imbalan bunga")) return "imbalan_bunga";
    return "dokumen";
  };

  // Download .docx from backend gateway with active session preservation and actionable errors
  const handleDownloadDoc = async (draftText, userPrompt, msgDocType = null, msgSessionId = null) => {
    if (!draftText) return;
    setDownloadingDoc(true);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:3000";
      let docType = (msgDocType && msgDocType !== "dokumen") ? msgDocType : null;
      if (!docType) {
        const fromUser = detectDocType(userPrompt);
        if (fromUser && fromUser !== "dokumen") {
          docType = fromUser;
        } else {
          const fromDraft = detectDocType(draftText);
          docType = (fromDraft && fromDraft !== "dokumen") ? fromDraft : "keberatan";
        }
      }
      const sessionId = msgSessionId || activeSessionId || null;
      console.log(`[DOWNLOAD] docType=${docType} | session_id=${sessionId} | resolvedFrom=${msgDocType ? 'msgDocType' : (detectDocType(userPrompt) !== 'dokumen' ? 'userPrompt' : 'draftText')}`);
      const res = await fetch(`${API_BASE}/api/generate-document`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          draft_text: draftText,
          doc_type: docType,
          filename: `Surat_${docType}_Tacia_${new Date().toISOString().slice(0,10)}`,
          session_id: sessionId,
        }),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        const detailedMsg =
          errorData.detail?.detail ||
          errorData.detail ||
          errorData.error ||
          `Server error: ${res.status}`;
        throw new Error(detailedMsg);
      }

      const blob = await res.blob();
      const disposition = res.headers.get("Content-Disposition") || "";
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
      const downloadName = filenameMatch
        ? filenameMatch[1]
        : `Surat_${docType}_${new Date().toISOString().slice(0,10)}.docx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = downloadName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download docx error:", err);
      alert(`Gagal download: ${err.message}`);
    } finally {
      setDownloadingDoc(false);
    }
  };

  // 1. Fetch Agent Info
  useEffect(() => {
    fetchAgentById(agentId).then(setFormData).catch(console.error);
  }, [agentId]);

  // Execute sending a normal chat message (used by handleSend and auto-send from dashboard)
  const sendMessage = async (questionText, baseMessages = null) => {
    const question = (questionText || "").trim();
    if (!question || loadingResponse) return;

    setInput("");

    const sentAt = new Date().toISOString();
    const currentBase = baseMessages || messages;
    const tempMessages = [...currentBase, { role: "user", content: question, timestamp: sentAt }];
    setMessages(tempMessages);
    setLoadingResponse(true);

    try {
      const promptInstruction = isEnglish
        ? (formData.agentPrompt ? `${formData.agentPrompt}\nPlease provide all legal analysis and answers in English.` : "You are TACIA, an expert AI tax consultant. Please provide all consultation and legal analysis in English.")
        : (formData.agentPrompt || "Kamu asisten AI perpajakan...");

      const payload = {
        question,
        language: isEnglish ? "en" : "id",
        business_id: agentId,
        session_id: activeSessionId,
        agent_personality: formData.agentPersonality || "professional",
        agent_prompt: promptInstruction,
        chat_history: tempMessages,
        business_name: formData.businessName,
        business_summary: formData.businessSummary,
      };

      const response = await runAgent(payload);
      let aiText = isEnglish ? "Sorry, error parsing response." : "Maaf, error parsing jawaban.";

      if (response?.data?.data?.answer) {
        aiText = response.data.data.answer;
      } else if (response?.data?.answer) {
        aiText = response.data.answer;
      } else if (response?.data?.output) {
        aiText = response.data.output;
      } else if (response?.data?.data?.output) {
        aiText = response.data.data.output;
      } else if (typeof response?.data === "string") {
        aiText = response.data;
      } else {
        console.error("[FRONTEND] Unexpected response structure:", response);
        aiText = isEnglish
          ? "Sorry, response format mismatch. Please check console for details."
          : "Maaf, format respons tidak sesuai. Silakan cek console untuk detail.";
      }

      const respData = response?.data || {};
      const innerData = respData.data || {};
      const resolvedSessionId = respData.session_id || innerData.session_id || null;
      const verificationStatus = respData.verification_status || innerData.verification_status || null;
      const missingCriticalFields = respData.missing_critical_fields || innerData.missing_critical_fields || [];
      const docType = respData.doc_type || innerData.doc_type || null;
      const resolvedIntent = respData.intent || innerData.intent || null;
      const docxFile = respData.docx_file || innerData.docx_file || null;

      if (resolvedSessionId) {
        setActiveSessionId(resolvedSessionId);
      }

      const analysisData = respData._analysis || innerData._analysis || null;
      const thinkingData = respData.thinking || innerData.thinking || [];

      const xmlContent = respData.xml_content || innerData.xml_content || null;
      const xmlFile = respData.xml_file || innerData.xml_file || null;

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: aiText,
          timestamp: new Date().toISOString(),
          _analysis: analysisData,
          thinking: thinkingData,
          intent: resolvedIntent,
          session_id: resolvedSessionId,
          verification_status: verificationStatus,
          missing_critical_fields: missingCriticalFields,
          doc_type: docType,
          docx_file: docxFile,
          xml_content: xmlContent,
          xml_file: xmlFile,
        }
      ]);

      // Auto-rename agent from "Chat Baru" to user's first question (like ChatGPT)
      const userMsgCount = tempMessages.filter(m => m.role === "user").length;
      if (userMsgCount === 1) {
        try {
          const title = question.length > 35 ? question.substring(0, 35) + "..." : question;
          await updateAgentById(agentId, { agentName: title });
          if (refreshAgents) refreshAgents();
        } catch (e) {
          console.error("Auto-rename failed:", e);
        }
      }

      // Track draft for .docx download
      if (isDraftComplete(aiText)) setLastDraft(aiText);
    } catch (err) {
      console.error("[FRONTEND] Chat Error:", err);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: isEnglish ? "Failed to connect to the server." : "Gagal terhubung ke server." },
      ]);
    } finally {
      setLoadingResponse(false);
    }
  };
  sendMessageRef.current = sendMessage;

  // 2. Fetch Chat History
  const loadHistory = useCallback(async () => {
    try {
      const history = await fetchChatHistory(agentId);
      let initialMsgs = [];
      if (history && history.length > 0) {
        initialMsgs = history.map((msg) => ({
          role: msg.sender === "user" ? "user" : "assistant",
          content: msg.message,
          timestamp: msg.timestamp || msg.created_at || null,
        }));
      } else {
        initialMsgs = [{
          role: "assistant",
          content: isEnglish
            ? "Welcome. Please state your tax consultation questions or dispute matters."
            : "Selamat datang. Silakan sampaikan pertanyaan Anda seputar perpajakan.",
          timestamp: new Date().toISOString()
        }];
      }
      setMessages(initialMsgs);

      // Check for pending prompt and files from dashboard (autoSend vs pre-fill)
      const pending = location.state?.initialPrompt || sessionStorage.getItem("tacia_pending_prompt");
      const shouldAutoSend = location.state?.autoSend || sessionStorage.getItem("tacia_auto_send") === "true";
      const initialFiles = location.state?.initialFiles;

      if (initialFiles && initialFiles.length > 0) {
        sessionStorage.removeItem("tacia_pending_prompt");
        sessionStorage.removeItem("tacia_auto_send");
        window.history.replaceState({}, document.title);

        if (shouldAutoSend && !autoSentRef.current) {
          autoSentRef.current = true;
          if (uploadFilesRef.current) {
            uploadFilesRef.current(initialFiles, pending || "", initialMsgs);
          }
        } else {
          setPendingFiles(initialFiles);
          if (pending) setInput(pending);
        }
      } else if (pending) {
        sessionStorage.removeItem("tacia_pending_prompt");
        sessionStorage.removeItem("tacia_auto_send");
        window.history.replaceState({}, document.title);

        if (shouldAutoSend && !autoSentRef.current) {
          autoSentRef.current = true;
          if (sendMessageRef.current) {
            sendMessageRef.current(pending, initialMsgs);
          }
        } else if (!shouldAutoSend) {
          setInput(pending);
          setTimeout(() => {
            if (textareaRef.current) {
              textareaRef.current.focus();
              textareaRef.current.setSelectionRange(pending.length, pending.length);
            }
          }, 250);
        }
      }
    } catch (error) {
      console.error("Gagal load history:", error);
    }
  }, [agentId, location.state]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({ top: chatContainerRef.current.scrollHeight, behavior: "smooth" });
    }
  }, [messages]);

  // Clear chat history
  const clearChat = async () => {
    if (window.confirm(isEnglish ? "Are you sure you want to permanently clear this chat history?" : "Yakin mau hapus permanen riwayat chat ini dari database?")) {
      try {
        await clearChatHistory(agentId);
        setMessages([
          {
            role: "assistant",
            content: isEnglish
              ? "Chat history has been cleared. Please start a new consultation."
              : "Riwayat percakapan telah dihapus. Silakan memulai konsultasi baru.",
          },
        ]);
      } catch (error) {
        console.error("Gagal hapus history:", error);
        alert(isEnglish ? "Failed to clear history." : "Gagal menghapus history.");
      }
    }
  };

  const handleUploadFiles = async (filesToSend, userMsg = "", baseHistory = null) => {
    if (!filesToSend || filesToSend.length === 0) return;
    setPendingFiles([]);
    setInput("");
    const sentAt = new Date().toISOString();

    const fileSummary = filesToSend.length === 1
      ? `[Lampiran Dokumen: ${filesToSend[0].name}]`
      : `[Lampiran: ${filesToSend.length} Dokumen]\n${filesToSend.map(f => `- ${f.name}`).join('\n')}`;

    const displayMsg = userMsg
      ? `${fileSummary}\n\n${userMsg}`
      : fileSummary;

    setMessages((prev) => [...(baseHistory || prev), { role: "user", content: displayMsg, timestamp: sentAt }]);
    setLoadingResponse(true);
    try {
      const result = await analyzeSurat(filesToSend, agentId, userMsg);
      const dataPayload = result?.data || result || {};
      const aiText = dataPayload?.analysis || dataPayload?.result || result?.analysis || "Gagal menganalisis dokumen.";
      const isDraft = Boolean(dataPayload?.is_draft_requested || dataPayload?.intent === "DOCUMENT_DRAFTING" || isDraftComplete(aiText));
      const docType = dataPayload?.doc_type || detectDocType(aiText);

      setMessages((prev) => [
        ...prev, 
        { 
          role: "assistant", 
          content: aiText, 
          timestamp: new Date().toISOString(), 
          isAnalysis: !isDraft,
          intent: isDraft ? "DOCUMENT_DRAFTING" : "CONSULTATION",
          doc_type: docType,
        }
      ]);

      if (isDraft) {
        setLastDraft(aiText);
      }
    } catch (err) {
      console.error("[FRONTEND] Upload Error:", err);
      const serverMsg = err?.response?.data?.error || err?.response?.data?.detail || err?.message || "Terjadi kesalahan saat memproses dokumen.";
      console.error("[FRONTEND] Server says:", serverMsg);
      setMessages((prev) => [...prev, { role: "assistant", content: `Gagal menganalisis dokumen: ${serverMsg}` }]);
    } finally {
      setLoadingResponse(false);
    }
  };
  uploadFilesRef.current = handleUploadFiles;

  // Send message
  const handleSend = async () => {
    // ── DOCUMENT UPLOAD MODE ──
    if (pendingFiles.length > 0) {
      return handleUploadFiles(pendingFiles, input.trim());
    }

    // ── NORMAL CHAT MODE ──
    if (!input.trim() || loadingResponse) return;
    sendMessage(input);
  };

  // Reload: re-send a user message (removes it + all subsequent, then re-sends)
  const handleReload = useCallback((questionText, msgIdx) => {
    if (loadingResponse) return;
    // Keep messages up to (not including) the user message being reloaded
    const kept = messages.slice(0, msgIdx);
    setMessages(kept);
    // Small delay so state settles, then re-send
    setTimeout(() => {
      sendMessage(questionText, kept);
    }, 50);
  }, [messages, loadingResponse, sendMessage]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const renderedMessages = useMemo(() => {
    return messages.map((msg, idx) => {
      // Check if date separator is needed
      const currentMsgDate = formatDateSeparator(msg.timestamp, isEnglish);
      const prevMsgDate = idx > 0 ? formatDateSeparator(messages[idx - 1].timestamp, isEnglish) : null;
      const showDateSeparator = currentMsgDate && currentMsgDate !== prevMsgDate;

      // Cari pesan user terdekat SEBELUM bubble AI ini (untuk detect doc_type)
      const prevUserMsg = msg.role === "assistant"
        ? messages.slice(0, idx).reverse().find(m => m.role === "user")?.content || ""
        : "";
      return (
      <div key={idx}>
        {showDateSeparator && (
          <div className="flex justify-center my-4">
            <span className="px-3 py-1 bg-surface-inset text-text-sec text-xs font-medium rounded-full border border-ledger-border shadow-xs">
              {currentMsgDate}
            </span>
          </div>
        )}
        <ChatBubble
          msg={msg}
          idx={idx}
          isLast={idx === messages.length - 1}
          isDownloadReady={isDownloadReady}
          handleDownloadDoc={handleDownloadDoc}
          downloadingDoc={downloadingDoc}
          formatTime={formatTime}
          preprocessMath={preprocessMath}
          mdComponents={mdComponents}
          userPrompt={prevUserMsg}
          onReload={handleReload}
          loadingResponse={loadingResponse}
        />
        {/* Analysis Panel — above feedback (ONLY FOR ADMIN / alfa271200@gmail.com) */}
        {isAdmin && msg.role === "assistant" && msg._analysis && (
          <AnalysisPanel analysis={msg._analysis} thinking={msg.thinking} />
        )}
        {/* Thinking Trace — for ALL users (no pipeline steps, just thinking) */}
        {!isAdmin && msg.role === "assistant" && msg.thinking && msg.thinking.length > 0 && (
          <AnalysisPanel analysis={msg._analysis} thinking={msg.thinking} hideSteps />
        )}
        {/* RLHF Feedback — hanya di AI bubble */}
        {msg.role === "assistant" && idx > 0 && (
          <FeedbackButtons
            messageId={msg.id || `msg-${idx}`}
            question={messages[idx - 1]?.content || ""}
            answer={typeof msg.content === "string" ? msg.content : JSON.stringify(msg.content)}
            agentId={agentId}
            intent={msg.intent}
          />
        )}
      </div>
    )});
  }, [messages, isAdmin, downloadingDoc, agentId, isEnglish]);

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    /*
     * Outer shell:
     *   • Mobile  → fills parent flush (no padding, no rounded corners)
     *   • Desktop → centred card with padding + rounded corners
     */
    <div className="flex h-full min-h-0 bg-paper">
      <div
        className={`flex min-h-0 flex-1 flex-col overflow-hidden bg-paper relative ${
          isDragging ? 'border-brand ring-2 ring-brand/30 sm:border-brand' : 'sm:border-ledger-border'
        }`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {/* Drop overlay */}
        {isDragging && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-paper/90 backdrop-blur-sm rounded-2xl">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-brand-low flex items-center justify-center">
                <PaperClipIcon className="w-8 h-8 text-brand animate-bounce" />
              </div>
              <p className="text-lg font-semibold text-text-pri">{t("chat.dropTitle")}</p>
              <p className="text-sm text-text-sec mt-1">{t("chat.dropSubtitle")}</p>
            </div>
          </div>
        )}

        {/* ── HEADER (Full Width Elegant) ── */}
        <div className="chat-header-bar px-4 sm:px-6 py-3 flex justify-between items-center z-10 shrink-0 w-full">
          <div className="flex items-center gap-3">
            <div className="chat-header-avatar w-9 h-9 sm:w-11 sm:h-11 rounded-full flex items-center justify-center shrink-0 p-1.5 sm:p-2">
              <img src={isLight ? "/tacia-icon.png" : "/tacia-icon-dark.png"} alt="Tacia" className="w-full h-full object-contain" />
            </div>
            <div className="flex flex-col justify-center">
              <h1 className="chat-header-title font-semibold text-base sm:text-lg leading-tight tracking-tight">
                {formData.agentName || "Tacia"}
              </h1>
              <p className="chat-header-subtitle text-[11px] sm:text-xs font-medium tracking-wide">
                {t("chat.aiSubtitle")}
              </p>
            </div>
          </div>

          <button
            onClick={clearChat}
            title={t("chat.clearHistory")}
            className="chat-header-action p-2 rounded-full transition-all duration-200"
          >
            <TrashIcon className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        {/* ── CHAT AREA ── */}
        <div
          ref={chatContainerRef}
          className="relative flex min-h-0 flex-1 flex-col space-y-3 overflow-y-auto overscroll-contain bg-paper px-3 py-4 sm:px-8 sm:py-6 max-w-4xl w-full mx-auto"
        >
          {/* Subtle dot pattern */}
          <div
            className="hidden pointer-events-none"
            style={{
              backgroundImage: "radial-gradient(circle, #94a3b8 1px, transparent 1px)",
              backgroundSize: "20px 20px",
            }}
          />

          {renderedMessages}

          {/* Thinking indicator — Claude-style step progression */}
          {loadingResponse && (
            <ThinkingIndicator
              question={
                messages.slice().reverse().find((m) => m.role === "user")?.content || ""
              }
            />
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* ── INPUT AREA ── */}
        {/*
         * Safe-area padding for iOS home bar via pb-safe (needs tailwind-safe-area plugin)
         * or just pb-2 on mobile as a fallback.
         */}
        <div className="shrink-0 bg-paper border-t border-ledger-border/60 px-3 pt-3 sm:px-8 max-w-4xl w-full mx-auto" style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}>

          {/* Multi-File preview strip */}
          {pendingFiles.length > 0 && (
            <div className="mb-2 p-2 bg-verified-low/80 border border-ledger-border rounded-sm text-xs">
              <div className="flex items-center justify-between mb-1.5 pb-1 border-b border-ledger-border/40 text-[11px] text-text-sec font-medium">
                <span>{pendingFiles.length} {t("chat.filesAttached")}</span>
                <button
                  type="button"
                  onClick={() => setPendingFiles([])}
                  className="text-text-sec hover:text-red-500 transition-colors"
                >
                  {t("chat.removeAll")}
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                {pendingFiles.map((file, idx) => (
                  <div
                    key={`${file.name}-${idx}`}
                    className="flex items-center gap-1.5 px-2.5 py-1 bg-paper border border-ledger-border rounded-sm text-text-pri font-medium shadow-2xs"
                  >
                    <DocumentTextIcon className="w-3.5 h-3.5 text-text-sec shrink-0" />
                    <span className="truncate max-w-[160px] sm:max-w-[220px]">{file.name}</span>
                    <span className="text-text-sec text-[10px]">({(file.size / 1024).toFixed(0)} KB)</span>
                    <button
                      type="button"
                      onClick={() => removePendingFile(idx)}
                      className="p-0.5 hover:bg-black/5 dark:hover:bg-white/10 rounded-full transition-colors"
                      title={t("chat.removeFile")}
                    >
                      <XMarkIcon className="w-3.5 h-3.5 text-text-sec hover:text-red-500" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            {/* Upload button */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.png,.jpg,.jpeg,.webp,.docx,.doc,.xlsx,.xls"
              className="hidden"
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  addPendingFiles(e.target.files);
                }
                e.target.value = "";
              }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={loadingResponse}
              title={t("chat.uploadTitle")}
              className="
                shrink-0 p-2.5 rounded-xl text-text-sec
                hover:bg-surface-hover hover:text-text-pri
                disabled:text-disabled disabled:cursor-not-allowed
                transition-colors
              "
            >
              <PaperClipIcon className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>

            <textarea
              ref={textareaRef}
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder={t("chat.inputPlaceholder")}
              disabled={loadingResponse}
              className="
                flex-1 px-4 py-2.5 rounded-xl border border-ledger-border bg-card-bg
                focus:outline-none focus:ring-1.5 focus:ring-text-pri focus:border-text-pri
                resize-none overflow-hidden scrollbar-none
                text-[14px] leading-relaxed text-text-pri placeholder:text-muted
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors shadow-xs
              "
              style={{ minHeight: "44px", maxHeight: "120px" }}
            />
            <button
              onClick={handleSend}
              disabled={(!input.trim() && pendingFiles.length === 0) || loadingResponse}
              className="
                shrink-0 p-2.5 rounded-xl bg-text-pri text-paper
                hover:opacity-90 active:scale-95
                disabled:bg-surface-inset disabled:text-muted disabled:border disabled:border-ledger-border disabled:cursor-not-allowed
                transition-all shadow-xs
              "
            >
              <PaperAirplaneIcon className="w-5 h-5 sm:w-6 sm:h-6 -rotate-45" />
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}